<template>
    <div
        v-if="options.length"
        v-click-outside="close"
        class="currency-select"
        :class="compact ? 'currency-select--compact' : 'currency-select--inline'"
    >
        <div class="currency-trigger" @click.stop="open = !open">
            <span>{{ current }}</span>
            <svg class="currency-chevron" :class="{ open }" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 3.5l3 3 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </div>
        <transition name="menu-drop">
            <div v-if="open" class="currency-dropdown">
                <div
                    v-for="opt in options"
                    :key="opt.value"
                    class="currency-row"
                    :class="{ active: current === opt.value }"
                    @click.stop="select(opt.value)"
                >
                    <span class="currency-row-code">{{ opt.label }}</span>
                    <svg v-if="current === opt.value" width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { updateMemberInfo } from '@/core/http/api';
import { loadCurrencyOptions } from './currency';

// Member currency picker. `compact` renders the small mobile button,
// otherwise it attaches to the right of the wallet button.
export default {
    name: 'NavCurrencySelect',
    props: {
        compact: { type: Boolean, default: false }
    },
    data() {
        return {
            open: false,
            options: [],
            saving: false
        };
    },
    computed: {
        current() {
            return this.$sharedState.memberInfo.currency || '';
        }
    },
    async created() {
        this.options = await loadCurrencyOptions();
    },
    methods: {
        close() {
            this.open = false;
        },
        async select(currency) {
            this.open = false;
            if (this.saving || this.current === currency) return;
            this.saving = true;
            try {
                const res = await updateMemberInfo({ currency });
                if (res.data?.error_code === '0') {
                    this.$root.$emit('fetch-member-info');
                } else {
                    this.$root.$emit('toast', { msg: res.data?.message || this.$t('profile.updateFailed'), type: 'error' });
                }
            } catch (err) {
                this.$root.$emit('toast', { msg: err.response?.data?.message || this.$t('profile.updateFailed'), type: 'error' });
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.currency-select {
    position: relative;
    flex-shrink: 0;
}

.currency-select--inline .currency-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 85px;
    padding: 7px 14px;
    border: 1px solid var(--primary);
    border-left: 1px solid var(--border-subtle);
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
    font-family: 'Urbanist', sans-serif;
    cursor: pointer;
    white-space: nowrap;

    @media (max-width: 768px) {
        padding: 6px 10px;
        font-size: 12px;
        min-width: 160px;
    }

    .currency-chevron {
        width: 9px;
        height: 9px;
        flex-shrink: 0;
        color: var(--text-dim);
    }
}

.currency-select--compact {
    display: none;

    @media (max-width: 768px) {
        display: flex;
    }

    .currency-trigger {
        background: var(--surface-control);
        color: var(--text);
        border-radius: 8px;
        height: 32px;
        padding: 0 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        white-space: nowrap;
        gap: 2px;
    }

    .currency-chevron {
        margin-left: 3px;
        transition: transform 0.2s ease;

        &.open {
            transform: rotate(180deg);
        }
    }
}

.currency-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 85px;
    padding: 6px;
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 12px;
    z-index: 400;
    box-shadow: 0 8px 24px color-mix(in srgb, var(--black) 50%, transparent);
    max-height: 260px;
    overflow-y: auto;
}

.currency-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
        background: var(--surface-hover);
    }

    svg {
        color: var(--highlight);
        flex-shrink: 0;
    }

    &.active .currency-row-code {
        color: var(--highlight);
        font-weight: 700;
    }
}

.currency-row-code {
    color: var(--text-sub);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.menu-drop-enter-active,
.menu-drop-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-drop-enter,
.menu-drop-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
